import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Home, User, Calendar, MessageSquare, Book, Phone, Bell,
  Heart, AlertTriangle, Clock, ChevronRight, Send, Pill,
  FileText, Users, Activity, RefreshCw, Loader2, CheckCircle,
  Star, Clipboard, PlayCircle, Smile, Package, Menu, X
} from "lucide-react";

const ICON_MAP = {
  heart: Heart,
  smile: Smile,
  clipboard: Clipboard,
  package: Package,
  phone: Phone,
  message: MessageSquare,
  alert: AlertTriangle,
  pill: Pill
};

export default function CaregiverMobileApp() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("home");
  const [homeData, setHomeData] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [resources, setResources] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [careTeam, setCareTeam] = useState(null);
  const [medications, setMedications] = useState(null);
  
  // Dialog states
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showSymptomDialog, setShowSymptomDialog] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [messageUrgency, setMessageUrgency] = useState("normal");
  const [symptomType, setSymptomType] = useState("");
  const [symptomSeverity, setSymptomSeverity] = useState([5]);
  const [symptomDescription, setSymptomDescription] = useState("");
  const [isSending, setIsSending] = useState(false);

  const loadHomeData = useCallback(async () => {
    try {
      const res = await api.get("/phase70/home");
      setHomeData(res.data);
    } catch (error) {
      console.error("Error loading home data:", error);
      // Set default data for demo
      setHomeData({
        patient: {
          name: "John Smith",
          condition: "Stable",
          last_visit: new Date(Date.now() - 3600000 * 5).toISOString(),
          next_visit: new Date(Date.now() + 3600000 * 24).toISOString(),
          care_team_message: "Pain well controlled. Continue current comfort measures."
        },
        quick_actions: [
          { id: "call_nurse", label: "Call Nurse", icon: "phone", urgent: false },
          { id: "message", label: "Send Message", icon: "message", urgent: false },
          { id: "symptom_report", label: "Report Symptom", icon: "alert", urgent: true },
          { id: "medication", label: "Medications", icon: "pill", urgent: false }
        ],
        recent_updates: [],
        upcoming_visits: [],
        unread_messages: 2,
        alerts: []
      });
    }
  }, []);

  const loadPatientData = useCallback(async () => {
    try {
      const res = await api.get("/phase70/patient");
      setPatientData(res.data);
    } catch (error) {
      console.error("Error loading patient data:", error);
    }
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const res = await api.get("/phase70/messages");
      setMessages(res.data.messages || []);
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  }, []);

  const loadResources = useCallback(async () => {
    try {
      const res = await api.get("/phase70/resources");
      setResources(res.data);
    } catch (error) {
      console.error("Error loading resources:", error);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const res = await api.get("/phase70/notifications");
      setNotifications(res.data.notifications || []);
    } catch (error) {
      console.error("Error loading notifications:", error);
    }
  }, []);

  const loadCareTeam = useCallback(async () => {
    try {
      const res = await api.get("/phase70/care-team");
      setCareTeam(res.data);
    } catch (error) {
      console.error("Error loading care team:", error);
    }
  }, []);

  const loadMedications = useCallback(async () => {
    try {
      const res = await api.get("/phase70/medications");
      setMedications(res.data);
    } catch (error) {
      console.error("Error loading medications:", error);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        loadHomeData(),
        loadPatientData(),
        loadMessages(),
        loadResources(),
        loadNotifications(),
        loadCareTeam(),
        loadMedications()
      ]);
      setLoading(false);
    };
    loadAll();
  }, [loadHomeData, loadPatientData, loadMessages, loadResources, loadNotifications, loadCareTeam, loadMedications]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadHomeData();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const handleSendMessage = async () => {
    if (!messageContent.trim()) {
      toast.error("Please enter a message");
      return;
    }
    setIsSending(true);
    try {
      await api.post("/phase70/messages", {
        content: messageContent,
        urgency: messageUrgency
      });
      toast.success("Message sent to care team");
      setMessageContent("");
      setMessageUrgency("normal");
      setShowMessageDialog(false);
      await loadMessages();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  const handleReportSymptom = async () => {
    if (!symptomType) {
      toast.error("Please select a symptom type");
      return;
    }
    setIsSending(true);
    try {
      const res = await api.post("/phase70/symptom-report", {
        symptom_type: symptomType,
        severity: symptomSeverity[0],
        description: symptomDescription
      });
      
      if (res.data.escalated) {
        toast.warning("High severity symptom - Care team has been alerted immediately");
      } else {
        toast.success("Symptom report submitted");
      }
      
      setSymptomType("");
      setSymptomSeverity([5]);
      setSymptomDescription("");
      setShowSymptomDialog(false);
    } catch (error) {
      toast.error("Failed to report symptom");
    } finally {
      setIsSending(false);
    }
  };

  const handleQuickAction = (actionId) => {
    switch (actionId) {
      case "call_nurse":
        if (careTeam?.care_team) {
          const nurse = careTeam.care_team.find(t => t.role === "On-Call Nurse");
          if (nurse) {
            window.location.href = `tel:${nurse.phone}`;
          }
        }
        break;
      case "message":
        setShowMessageDialog(true);
        break;
      case "symptom_report":
        setShowSymptomDialog(true);
        break;
      case "medication":
        setActiveTab("medications");
        break;
      default:
        break;
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-teal-50 to-white" data-testid="caregiver-pwa-loading">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto" />
          <p className="text-slate-600 font-medium">Loading your care portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white" data-testid="caregiver-pwa">
      {/* Mobile Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">Goodwin Cares</h1>
              <p className="text-xs text-slate-500">Family Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="w-5 h-5" />
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pb-20 px-4 max-w-lg mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          {/* Home Tab */}
          <TabsContent value="home" className="space-y-4 mt-0">
            {/* Patient Status Card */}
            {homeData?.patient && (
              <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-600 to-teal-700" data-testid="patient-status-card">
                <CardContent className="p-5 text-white">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-teal-100 text-sm">Your Loved One</p>
                      <h2 className="text-xl font-bold mt-1">{homeData.patient.name}</h2>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className="bg-white/20 text-white border-0">
                          {homeData.patient.condition}
                        </Badge>
                      </div>
                    </div>
                    <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                      <User className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-white/10 rounded-lg">
                    <p className="text-sm text-teal-100">Latest from Care Team:</p>
                    <p className="text-sm mt-1">{homeData.patient.care_team_message}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="bg-white/10 rounded-lg p-3">
                      <p className="text-xs text-teal-100">Last Visit</p>
                      <p className="text-sm font-medium mt-1">{formatTime(homeData.patient.last_visit)}</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                      <p className="text-xs text-teal-100">Next Visit</p>
                      <p className="text-sm font-medium mt-1">{formatTime(homeData.patient.next_visit)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-4 gap-2" data-testid="quick-actions">
              {homeData?.quick_actions?.map((action) => {
                const IconComponent = ICON_MAP[action.icon] || Heart;
                return (
                  <button
                    key={action.id}
                    onClick={() => handleQuickAction(action.id)}
                    className={`flex flex-col items-center p-3 rounded-xl transition-all ${
                      action.urgent 
                        ? "bg-red-50 hover:bg-red-100 border border-red-200"
                        : "bg-white hover:bg-slate-50 border border-slate-200"
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                      action.urgent ? "bg-red-100" : "bg-teal-100"
                    }`}>
                      <IconComponent className={`w-5 h-5 ${action.urgent ? "text-red-600" : "text-teal-600"}`} />
                    </div>
                    <span className="text-xs font-medium text-slate-700 text-center">{action.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Alerts */}
            {homeData?.alerts?.length > 0 && (
              <div className="space-y-2">
                {homeData.alerts.map((alert) => (
                  <Card key={alert.id} className="border-yellow-200 bg-yellow-50">
                    <CardContent className="p-3 flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                      <p className="text-sm text-yellow-800">{alert.message}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Recent Updates */}
            <Card data-testid="recent-updates">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-bold text-slate-900">Recent Updates</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {homeData?.recent_updates?.length > 0 ? (
                  homeData.recent_updates.slice(0, 3).map((update) => (
                    <div key={update.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <Activity className="w-4 h-4 text-teal-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-slate-900">{update.title}</p>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{update.description}</p>
                        <p className="text-xs text-slate-400 mt-1">{formatTime(update.timestamp)}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">No recent updates</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Patient Tab */}
          <TabsContent value="patient" className="space-y-4 mt-0">
            {patientData?.patient && (
              <>
                <Card data-testid="patient-details">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-bold text-slate-900">Patient Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Name</p>
                        <p className="font-medium text-slate-900">{patientData.patient.name}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Age</p>
                        <p className="font-medium text-slate-900">{patientData.patient.age} years</p>
                      </div>
                      <div className="col-span-2 p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Diagnosis</p>
                        <p className="font-medium text-slate-900">{patientData.patient.diagnosis}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Days on Service</p>
                        <p className="font-medium text-slate-900">{patientData.patient.days_on_service}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="current-status">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-bold text-slate-900">Current Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-teal-50 rounded-lg">
                        <p className="text-xs text-teal-600">Pain Level</p>
                        <p className="text-2xl font-bold text-teal-700">{patientData.current_status?.pain_level}/10</p>
                      </div>
                      <div className="p-3 bg-teal-50 rounded-lg">
                        <p className="text-xs text-teal-600">Comfort</p>
                        <p className="font-medium text-teal-700">{patientData.current_status?.comfort_score}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Alertness</p>
                        <p className="font-medium text-slate-700">{patientData.current_status?.alertness}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Appetite</p>
                        <p className="font-medium text-slate-700">{patientData.current_status?.appetite}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="care-plan">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-bold text-slate-900">Care Plan Focus</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {patientData.care_plan_summary?.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-slate-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Medications Tab */}
          <TabsContent value="medications" className="space-y-4 mt-0">
            <Card data-testid="medication-list">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-bold text-slate-900">Current Medications</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {medications?.medications?.map((med) => (
                  <div key={med.id} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{med.name}</p>
                        <p className="text-xs text-slate-500 mt-1">{med.frequency}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {med.purpose}
                      </Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card data-testid="medication-schedule">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-bold text-slate-900">Today's Schedule</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {medications?.schedule?.map((slot, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      slot.given ? "bg-green-100" : "bg-slate-200"
                    }`}>
                      {slot.given ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <Clock className="w-4 h-4 text-slate-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm text-slate-900">{slot.time}</p>
                      <p className="text-xs text-slate-500">{slot.medications?.join(", ")}</p>
                    </div>
                    {slot.given && <Badge className="bg-green-100 text-green-700 border-0">Given</Badge>}
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-4 mt-0">
            <div className="flex justify-between items-center">
              <h2 className="font-bold text-slate-900">Messages</h2>
              <Button size="sm" onClick={() => setShowMessageDialog(true)}>
                <Send className="w-4 h-4 mr-2" />
                New Message
              </Button>
            </div>
            
            <div className="space-y-2" data-testid="message-list">
              {messages.length > 0 ? (
                messages.map((msg) => (
                  <Card key={msg.id} className={!msg.read ? "border-teal-200 bg-teal-50/50" : ""}>
                    <CardContent className="p-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm text-slate-900">{msg.sender}</p>
                            {!msg.read && <span className="w-2 h-2 bg-teal-500 rounded-full" />}
                          </div>
                          <p className="font-medium text-sm text-slate-700 mt-1">{msg.subject}</p>
                          <p className="text-xs text-slate-500 mt-1 line-clamp-1">{msg.preview}</p>
                          <p className="text-xs text-slate-400 mt-2">{formatTime(msg.timestamp)}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <MessageSquare className="w-10 h-10 text-slate-300 mx-auto" />
                    <p className="text-slate-500 mt-2">No messages yet</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources" className="space-y-4 mt-0">
            <h2 className="font-bold text-slate-900">Caregiver Resources</h2>
            
            <div className="grid grid-cols-2 gap-3" data-testid="resource-categories">
              {resources?.categories && Object.entries(resources.categories).map(([key, category]) => {
                const IconComponent = ICON_MAP[category.icon] || Book;
                return (
                  <Card key={key} className="hover:border-teal-200 cursor-pointer transition-colors">
                    <CardContent className="p-4 text-center">
                      <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center mx-auto">
                        <IconComponent className="w-6 h-6 text-teal-600" />
                      </div>
                      <p className="font-medium text-sm text-slate-900 mt-3">{category.name}</p>
                      <p className="text-xs text-slate-500 mt-1">{category.resources?.length || 0} resources</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team" className="space-y-4 mt-0">
            <h2 className="font-bold text-slate-900">Your Care Team</h2>
            
            <div className="space-y-2" data-testid="care-team-list">
              {careTeam?.care_team?.map((member, idx) => (
                <Card key={idx}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                          <Users className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{member.name}</p>
                          <p className="text-xs text-slate-500">{member.role}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {member.available && (
                          <Badge className="bg-green-100 text-green-700 border-0 text-xs">Available</Badge>
                        )}
                        <a href={`tel:${member.phone}`}>
                          <Button size="sm" variant="outline">
                            <Phone className="w-4 h-4" />
                          </Button>
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {careTeam?.emergency && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-red-800">Emergency: {careTeam.emergency.number}</p>
                      <p className="text-xs text-red-600 mt-1">{careTeam.emergency.note}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-2 py-2 z-50">
        <div className="flex justify-around max-w-lg mx-auto">
          {[
            { id: "home", icon: Home, label: "Home" },
            { id: "patient", icon: User, label: "Patient" },
            { id: "medications", icon: Pill, label: "Meds" },
            { id: "messages", icon: MessageSquare, label: "Messages", badge: messages.filter(m => !m.read).length },
            { id: "resources", icon: Book, label: "Resources" },
            { id: "team", icon: Users, label: "Team" }
          ].map((item) => {
            const IconComponent = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`flex flex-col items-center p-2 rounded-lg transition-colors relative ${
                  isActive ? "text-teal-600" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <IconComponent className={`w-5 h-5 ${isActive ? "text-teal-600" : ""}`} />
                <span className="text-xs mt-1 font-medium">{item.label}</span>
                {item.badge > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {item.badge}
                  </span>
                )}
                {isActive && <div className="absolute -bottom-2 w-1 h-1 bg-teal-600 rounded-full" />}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Send Message to Care Team</DialogTitle>
            <DialogDescription>Your message will be sent to the on-duty nurse.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-700">Urgency</Label>
              <Select value={messageUrgency} onValueChange={setMessageUrgency}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">Message</Label>
              <Textarea
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                placeholder="Type your message..."
                className="mt-1"
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageDialog(false)}>Cancel</Button>
            <Button onClick={handleSendMessage} disabled={isSending}>
              {isSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Symptom Report Dialog */}
      <Dialog open={showSymptomDialog} onOpenChange={setShowSymptomDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Report a Symptom</DialogTitle>
            <DialogDescription>Let the care team know about any changes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-700">Symptom Type</Label>
              <Select value={symptomType} onValueChange={setSymptomType}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select symptom..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pain">Pain</SelectItem>
                  <SelectItem value="breathing">Breathing Difficulty</SelectItem>
                  <SelectItem value="nausea">Nausea/Vomiting</SelectItem>
                  <SelectItem value="anxiety">Anxiety/Restlessness</SelectItem>
                  <SelectItem value="fever">Fever</SelectItem>
                  <SelectItem value="skin">Skin Changes</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">Severity (1-10)</Label>
              <div className="flex items-center gap-4 mt-2">
                <Slider
                  value={symptomSeverity}
                  onValueChange={setSymptomSeverity}
                  min={1}
                  max={10}
                  step={1}
                  className="flex-1"
                />
                <span className={`font-bold text-lg w-8 text-center ${
                  symptomSeverity[0] >= 7 ? "text-red-600" : symptomSeverity[0] >= 4 ? "text-yellow-600" : "text-green-600"
                }`}>
                  {symptomSeverity[0]}
                </span>
              </div>
              {symptomSeverity[0] >= 7 && (
                <p className="text-xs text-red-600 mt-1">High severity - Care team will be alerted immediately</p>
              )}
            </div>
            <div>
              <Label className="text-slate-700">Description (optional)</Label>
              <Textarea
                value={symptomDescription}
                onChange={(e) => setSymptomDescription(e.target.value)}
                placeholder="Describe what you're observing..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSymptomDialog(false)}>Cancel</Button>
            <Button onClick={handleReportSymptom} disabled={isSending}>
              {isSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
              Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
